import { ref, reactive, onUnmounted, watch, computed, toValue } from "vue";
import { useStorage } from "@vueuse/core";
import type { IModalProps } from "@magnit/core/src/components/VModal/VModal.types";
import type {
  IUseAuthResolvePayload,
  IUseAuthOptions,
  IFetchOTPCodeBody,
  IVerifyOTPCodeBody,
  IRegisterBody,
} from "../types";
import {
  STORAGE_ATTEMPT_ID,
  STORAGE_EXPIRE,
  STORAGE_PHONE_NUMBER,
  LABEL_REJECT_DEFAULT_REASON,
  LABEL_REJECT_REASON_UNMOUNT,
  LABEL_REJECT_REASON_CLOSE,
  DEFAULT_KFP_CID,
  DEFAULT_CAPTCHA_KEY,
} from "../constants";
import useAuthCookies from "./useAuthCookies";
import useAuthApi from "./useAuthApi";

export default (opts: IUseAuthOptions) => {
  const form = ref<"phone" | "code">("phone");
  const error = ref(false);
  const pending = ref(false);
  const success = ref(false);
  const errorText = ref("");
  const errorResponse = ref<unknown | null>(null);
  const errorTarget = ref<string | null>(null);
  const attemptId = useStorage<string>(STORAGE_ATTEMPT_ID, "");
  const expires = useStorage(STORAGE_EXPIRE, -1);
  const phoneNumber = useStorage(STORAGE_PHONE_NUMBER, "");
  const modal = reactive<Partial<IModalProps>>({});
  const codeLength = ref<4 | 5 | 6>(5);
  const authModalOpen = ref(false);

  const {
    getKfpTokenCookie,
    getMagnitCodeCookie,
    getAccessTokenCookie,
    getRefreshTokenCookie,
    setKfpTokenCookie,
    setMagnitCodeCookie,
    setJWTToken,
    removeKfpTokenCookie,
    removeMagnitCodeCookie,
    removeJWTToken,
    isAuthorized,
  } = useAuthCookies(opts);
  const {
    authorizeUser: apiAuthorizeUser,
    fetchOtpCodeByPhone: apiFetchOtpCodeByPhone,
    verifyOtpCode: apiVerifyOtpCode,
    fetchProfile: apiFetchProfile,
    register: apiRegister,
    checkUserProfile: apiCheckProfile,
  } = useAuthApi(opts);

  let authPromise: null | {
    resolve: (v: IUseAuthResolvePayload) => void;
    reject: (reason?: Error) => void;
  } = null;

  const fetchKFPToken = async () => {
    try {
      const kfpCid = opts?.kfpCid || DEFAULT_KFP_CID;
      // kfp может быть не доступен
      const ksid =
        (await (
          window as unknown as {
            kfp?: {
              login_start: (
                kfpCid: string,
                meta: string,
              ) => Promise<string | null>;
            };
          }
        ).kfp?.login_start(kfpCid, "login")) ?? null;
      if (ksid) setKfpTokenCookie(ksid);
    } catch (err) {
      console.error(`Ошибка при попытке получить KFP токен: ${err}`);
    }
  };
  const fetchTokens = async () => {
    const response = await apiAuthorizeUser({
      magnitIDCode: getMagnitCodeCookie(),
    });
    setJWTToken({
      access: response!.accessToken,
      refresh: response!.refreshToken,
    });
    return response;
  };
  const fetchAuthCode = async (body: IFetchOTPCodeBody) => {
    await fetchKFPToken();
    const response = await apiFetchOtpCodeByPhone(body);
    attemptId.value = response!.attemptId;
    expires.value = new Date(response!.expires).getTime();
  };
  const verifyCode = async (body: IVerifyOTPCodeBody) => {
    const response = await apiVerifyOtpCode(body);
    setMagnitCodeCookie(response!.magnitIDCode);
    return response;
  };
  const registerProfile = async (body: Omit<IRegisterBody, "magnitIDCode">) => {
    const payload = {
      ...body,
      magnitIDCode: getMagnitCodeCookie(),
    };
    const response = await apiRegister(payload);
    return response;
  };

  const unmountAuthModal = () => {
    authModalOpen.value = false;
    if (authPromise !== null)
      authPromise.reject(new Error(LABEL_REJECT_REASON_UNMOUNT));
    authPromise = null;
  };
  const authorizeReject = (reason = LABEL_REJECT_DEFAULT_REASON) => {
    if (authPromise === null) return;
    authPromise.reject(
      new Error(`util-auth/useAuth/authrize/reject: ${reason}`),
    );
    authPromise = null;
    unmountAuthModal();
  };
  const authrizeResolve = (result: IUseAuthResolvePayload) => {
    if (authPromise === null) return;
    authPromise.resolve(result);
    authPromise = null;
    unmountAuthModal();
  };
  const onBeforeRequest = () => {
    pending.value = true;
    error.value = false;
    success.value = false;
    errorResponse.value = null;
    errorTarget.value = null;
  };
  const reset = () => {
    onBeforeRequest();
    form.value = "phone";
    expires.value = -1;
    pending.value = false;
  };
  const onSubmitPhone = async (payload: {
    phone: string;
    captchaToken: string;
  }) => {
    const { phone } = payload;
    const prevPhone = phoneNumber.value;
    phoneNumber.value = phone;
    if (phoneNumber.value === prevPhone && Date.now() < expires.value) {
      form.value = "code";
      return;
    }
    onBeforeRequest();
    const body = {
      phoneNumber: `7${phoneNumber.value}`,
      captchaToken: payload.captchaToken,
      aud: opts?.aud,
    };
    try {
      await fetchAuthCode(body);
      form.value = "code";
    } catch (err) {
      errorResponse.value = err;
      errorTarget.value = "phone-submit";
      error.value = true;
    } finally {
      pending.value = false;
    }
  };
  const fetchProfile = async () => {
    try {
      pending.value = true;
      const response = await apiFetchProfile();
      return response;
    } catch (err) {
      errorResponse.value = err;
      errorTarget.value = "fetch-profile";
      error.value = true;
    } finally {
      pending.value = false;
    }
  };
  const switchToNewMacrotask = () =>
    new Promise((resolve) => setTimeout(resolve, opts.techTimeout ?? 50));
  const onSubmitCode = async (payload: { code: string }) => {
    onBeforeRequest();
    const body = {
      code: payload.code,
      attemptId: attemptId.value,
      phoneNumber: `7${phoneNumber.value}`,
      aud: opts?.aud,
    };
    try {
      const response = await verifyCode(body);
      success.value = true;
      if (!response) throw new Error("Пустой ответ верификации кода");
      if (!response.isRegistered) return authrizeResolve(response);
      await fetchTokens();
      removeMagnitCodeCookie();
      // в `build` версии nuxt проекта, без этой строчки, первый запрос на получение профиля падает из-за отсутствия Authorization токена
      // он проставляется в document.cookie, но CookieRef не успевает обновиться, будто его обновление происходит в отдельной микротаске
      // на dev сборке такое поведение не выявлено
      await switchToNewMacrotask();
      const profile = await fetchProfile();
      return authrizeResolve({ ...response, profile });
    } catch (err) {
      errorResponse.value = err;
      errorTarget.value = "verify-code";
      error.value = true;
    } finally {
      pending.value = false;
    }
  };

  if (!opts.skipLifeCycleHooks) {
    onUnmounted(() => {
      authorizeReject(LABEL_REJECT_REASON_UNMOUNT);
    });
  }

  const authModalProps = computed(() => ({
    form: toValue(form),
    error: opts?.antiFraud ? false : toValue(error),
    pending: toValue(pending),
    success: toValue(success),
    errorText: toValue(errorText),
    expires: toValue(expires),
    modal: {
      ...toValue(modal),
      open: authModalOpen.value,
      active: authModalOpen.value,
    },
    phone: toValue(phoneNumber),
    codeLength: toValue(codeLength),
    captchaKey: opts?.captchaKey ?? DEFAULT_CAPTCHA_KEY,
    onClose: () => authorizeReject(LABEL_REJECT_REASON_CLOSE),
    "onUpdate:form": (next: "code" | "phone") => (form.value = next),
    "onSubmit:phone": onSubmitPhone,
    "onSubmit:code": onSubmitCode,
  }));

  const authorize = (): Promise<IUseAuthResolvePayload> => {
    return new Promise((resolve, reject) => {
      authPromise = { resolve, reject };
      authModalOpen.value = true;
    });
  };

  watch(authModalOpen, (next) => !next && reset());
  if (import.meta.env.DEV) {
    watch(errorResponse, (next) => {
      if (!next) return;
      console.error("util-auth/useAuth ошибка при отправке запроса: ", next);
    });
  }

  return {
    // useAuthApi
    apiAuthorizeUser,
    apiFetchOtpCodeByPhone,
    apiVerifyOtpCode,
    apiFetchProfile,
    apiRegister,
    apiCheckProfile,
    // /useAuthApi
    // useAuthCookies
    getKfpTokenCookie,
    getMagnitCodeCookie,
    getAccessTokenCookie,
    getRefreshTokenCookie,
    setKfpTokenCookie,
    setMagnitCodeCookie,
    setJWTToken,
    removeKfpTokenCookie,
    removeMagnitCodeCookie,
    removeJWTToken,
    isAuthorized,
    // /useAuthCookies
    form,
    error,
    errorResponse,
    errorTarget,
    pending,
    success,
    errorText,
    expires,
    phoneNumber,
    modal,
    codeLength,

    authModalOpen,
    authModalProps,

    fetchKFPToken,
    fetchTokens,
    fetchAuthCode,
    fetchProfile,
    verifyCode,
    registerProfile,

    authorize,
  };
};
